import { recipes, activities, stories } from '../data/content';
import { getUpcomingHolidays } from './hebrewCalendar';
import { generateConversationStarters } from './conversationStarters';
import { calculateProgress } from './experiences';

/**
 * Generate personalized feed items based on user context
 * @param {Object} family - Family data
 * @param {Array} experiences - User's experiences
 * @param {Array} conversations - Recent conversations
 * @param {number} limit - Maximum number of items to return
 * @returns {Array} Array of feed items with type, priority, and data
 */
export function generatePersonalizedFeed(family, experiences = [], conversations = [], limit = 20) {
  const feedItems = [];
  
  // Get active experiences (not completed or archived, and in the future)
  const activeExperiences = experiences.filter(exp => {
    if (exp.status === 'completed' || exp.status === 'archived' || !exp.currentPhase) {
      return false;
    }
    // Only include future experiences
    if (exp.date) {
      const expDate = exp.date?.toDate ? exp.date.toDate() : new Date(exp.date);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      expDate.setHours(0, 0, 0, 0);
      return expDate >= today;
    }
    return true; // Include experiences without dates (planning phase)
  });

  // Get upcoming holidays
  const upcomingHolidays = getUpcomingHolidays(3);

  // 1. Continue active experiences (HIGH PRIORITY - priority 10)
  activeExperiences
    .sort((a, b) => {
      // Sort by: soonest date first, then most recently updated
      const dateA = a.date?.toDate ? a.date.toDate() : new Date(a.date || 0);
      const dateB = b.date?.toDate ? b.date.toDate() : new Date(b.date || 0);
      if (dateA.getTime() !== dateB.getTime()) {
        return dateA - dateB; // Soonest first
      }
      const updatedA = a.updatedAt?.toDate ? a.updatedAt.toDate() : new Date(a.updatedAt || 0);
      const updatedB = b.updatedAt?.toDate ? b.updatedAt.toDate() : new Date(b.updatedAt || 0);
      return updatedB - updatedA; // Most recent first
    })
    .slice(0, 2)
    .forEach(exp => {
      feedItems.push({
        type: 'continue-experience',
        priority: 10,
        data: exp,
        id: exp.id,
      });
    });

  // 2. Start new experience suggestions (based on upcoming holidays) - priority 9
  upcomingHolidays.forEach(holiday => {
    if (holiday.daysUntil < 60 && holiday.daysUntil > 7 && holiday.isMajorHoliday) {
      feedItems.push({
        type: 'start-experience',
        priority: 9,
        data: {
          holiday: holiday.name,
          daysUntil: holiday.daysUntil,
          suggestedType: mapHolidayToExperienceType(holiday.name),
        },
      });
    }
  });

  // 3. Resume old chats - priority 8
  conversations
    .filter(conv => !conv.archived && !conv.favorite)
    .slice(0, 2)
    .forEach(chat => {
      feedItems.push({
        type: 'resume-chat',
        priority: 8,
        data: chat,
        id: chat.id,
      });
    });

  // 4. AI conversation starters (personalized) - priority 7
  const starters = generateConversationStarters(family, experiences);
  starters.slice(0, 2).forEach(starter => {
    feedItems.push({
      type: 'ai-starter',
      priority: 7,
      data: starter,
    });
  });

  // 5. Content library items (personalized) - priority 5-6
  const contentItems = getPersonalizedContent(family, activeExperiences);
  feedItems.push(...contentItems);

  // Sort by priority, then shuffle within priority groups
  feedItems.sort((a, b) => b.priority - a.priority);
  
  // Mix it up (don't show 5 recipes in a row)
  const mixed = interleaveByType(feedItems);
  
  return mixed.slice(0, limit);
}

/**
 * Get personalized content from library
 */
function getPersonalizedContent(family, activeExperiences) {
  const items = [];
  const kidsAges = family?.kids?.map(k => k.age) || [];
  
  // Get all content
  const allContent = [
    ...recipes.map(r => ({ ...r, contentType: 'recipe' })),
    ...activities.map(a => ({ ...a, contentType: 'activity' })),
    ...stories.map(s => ({ ...s, contentType: 'story' })),
  ];

  allContent.forEach(content => {
    let score = 5; // Base priority

    // Boost if related to active experience
    activeExperiences.forEach(exp => {
      if (content.tags?.includes(exp.type) || 
          content.title?.toLowerCase().includes(exp.type?.toLowerCase() || '')) {
        score += 3;
      }
    });

    // Boost if age-appropriate
    if (content.ageRange && kidsAges.length > 0) {
      const [minAge, maxAge] = parseAgeRange(content.ageRange);
      if (kidsAges.some(age => age >= minAge && age <= maxAge)) {
        score += 2;
      }
    }

    // Boost if high community rating (if we had claps)
    if (content.totalClaps > 50) score += 1;
    if (content.totalClaps > 100) score += 2;

    items.push({
      type: content.contentType, // 'recipe' | 'activity' | 'story'
      priority: score,
      data: content,
      id: content.id,
    });
  });

  return items;
}

/**
 * Parse age range string (e.g., "5-10" or "All ages")
 */
function parseAgeRange(ageRange) {
  if (!ageRange || ageRange === 'All ages') {
    return [0, 18]; // All ages
  }
  const parts = ageRange.split('-').map(Number);
  return [parts[0] || 0, parts[1] || 18];
}

/**
 * Map holiday name to experience type
 */
export function mapHolidayToExperienceType(holidayName) {
  const name = holidayName.toLowerCase();
  if (name.includes('passover') || name.includes('pesach')) return 'passover-seder';
  if (name.includes('hanukkah') || name.includes('chanukah')) return 'hanukkah';
  if (name.includes('rosh hashana')) return 'rosh-hashanah';
  if (name.includes('yom kippur')) return 'yom-kippur';
  if (name.includes('sukkot')) return 'sukkot';
  if (name.includes('purim')) return 'purim';
  if (name.includes('shavuot')) return 'shavuot';
  if (name.includes('tu bishvat') || name.includes('tu b\'shevat')) return 'shabbat'; // Tu Bishvat can use shabbat experience type
  if (name.includes('lag baomer')) return 'shabbat'; // Lag BaOmer can use shabbat experience type
  return 'shabbat'; // Default
}

/**
 * Interleave items by type to avoid showing same type consecutively
 */
function interleaveByType(items) {
  const buckets = {
    'recipe': [],
    'activity': [],
    'story': [],
    'continue-experience': [],
    'start-experience': [],
    'resume-chat': [],
    'ai-starter': [],
  };

  items.forEach(item => {
    if (buckets[item.type]) {
      buckets[item.type].push(item);
    }
  });

  const mixed = [];
  let index = 0;
  const maxLength = Math.max(...Object.values(buckets).map(b => b.length));

  while (mixed.length < items.length && index < maxLength) {
    Object.keys(buckets).forEach(type => {
      if (buckets[type][index]) {
        mixed.push(buckets[type][index]);
      }
    });
    index++;
  }

  return mixed;
}

